import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Monitor, MousePointerClick, RotateCcw } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { useWorkshopScreen } from '../../hooks/useWorkshopScreen';
import { WorkshopScreenViewer } from './WorkshopScreenViewer';

const BUTTON =
  'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-[9px] px-2.5 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-desk-focus disabled:cursor-not-allowed disabled:opacity-50';
/** « Voir l'écran »: a link-like gesture, readable on the strip's tint as on a white card. */
const LINK = 'text-desk-strong hover:bg-desk-hover';
const NEUTRAL =
  'bg-desk-card text-desk-ink ring-1 ring-inset ring-desk-ring hover:bg-desk-hover';
const ACT =
  'bg-desk-card text-desk-ink ring-1 ring-inset ring-desk-ring hover:bg-desk-hover';

interface Props {
  /** The mission the screen is opened for (journal), null from the AI cockpit. */
  threadId: string | null;
  /** The gate's public address is set: the screen can be opened at all. */
  available: boolean;
  /** Decided by the database (`screen_controls`): the user may act in the browser now. */
  controlsAllowed: boolean;
  /** Leads the row of gestures (the workshop strip: who drives, and what it means). */
  lead?: ReactNode;
  /** Ends the row of gestures (the strip's « Prendre la main »). */
  trail?: ReactNode;
  /** Connect as soon as it is shown (the header's window): to act if allowed, else to watch. */
  autoOpen?: boolean;
  /**
   * A gesture on the screen (PLAN-ATELIER-OTTO § 17). Clicking « Ouvrir l'écran pour agir » also
   * reopens the browser to every site when a workshop left it restricted, and « Ouvrir le navigateur à
   * tout site » needs no confirmation: the gesture IS to log in, nothing is in progress to lose. An
   * automatic opening never reopens: the card may be shown twice, a restart mid-login would cut it.
   */
  forGesture?: boolean;
}

/**
 * The workshop's screen (PLAN-ATELIER-OTTO § 16.6), on demand: watch the forms Chrome live, or act
 * in it — finish a form, tick a consent, send — when the database says so. Its gestures sit on one
 * row with what the strip puts around them (mock-up « Formulaire » : En pause · Voir l'écran ·
 * Prendre la main); the live screen opens under that row.
 */
export function WorkshopScreenPanel({
  threadId,
  available,
  controlsAllowed,
  lead,
  trail,
  autoOpen = false,
  forGesture = false,
}: Props) {
  const { phase, screen, error, notice, reopening, open, close, reopenForms } = useWorkshopScreen(threadId);
  const [confirmReopen, setConfirmReopen] = useState(false);
  const autoOpened = useRef(false);
  const row = 'flex flex-wrap items-center gap-x-2.5 gap-y-2';

  // A gesture: the right to act may go (another mission's workshop takes the browser). The screen
  // opened to act is then closed, so that nothing typed in it reaches the workshop (audit 08/10/2026).
  const lostControls = forGesture && screen !== null && !screen.viewOnly && !controlsAllowed;
  useEffect(() => {
    if (lostControls) close("Un atelier formulaire vient de prendre le navigateur : l'écran est fermé pour que rien de ce que tu tapes ne lui parvienne. Rouvre-le à la fin de l'atelier.");
  }, [lostControls, close]);

  useEffect(() => {
    if (!autoOpen || !available || autoOpened.current) return;
    autoOpened.current = true;
    void open(controlsAllowed);
  }, [autoOpen, available, controlsAllowed, open]);

  if (!available) {
    return (
      <div className={row}>
        {lead}
        <p className="text-xs text-desk-muted">L'écran de l'atelier n'est pas encore en service.</p>
        {trail}
      </div>
    );
  }
  const opening = phase === 'opening';
  return (
    <div className="space-y-2.5">
      <div className={row}>
        {lead}
        {!screen && (
          <button type="button" disabled={opening} onClick={() => void open(false)} className={twMerge(BUTTON, LINK)}>
            <Monitor size={15} aria-hidden="true" /> {opening ? 'Ouverture…' : "Voir l'écran"}
          </button>
        )}
        {controlsAllowed && (!screen || screen.viewOnly) && (
          <button
            type="button"
            disabled={opening}
            onClick={() => void open(true, { reopenIfRestricted: forGesture })}
            className={twMerge(BUTTON, ACT)}
          >
            <MousePointerClick size={15} aria-hidden="true" /> {screen ? 'Agir dans cet écran' : "Ouvrir l'écran pour agir"}
          </button>
        )}
        {screen && !screen.viewOnly && screen.formsOpen !== true && !confirmReopen && (
          <button
            type="button"
            disabled={reopening}
            onClick={() => (forGesture ? void reopenForms() : setConfirmReopen(true))}
            className={twMerge(BUTTON, forGesture ? ACT : NEUTRAL)}
          >
            <RotateCcw size={15} aria-hidden="true" />
            {reopening ? 'Réouverture…' : forGesture ? 'Ouvrir le navigateur à tout site' : 'Rouvrir à tout site'}
          </button>
        )}
        {screen && confirmReopen && (
          <span className="flex flex-wrap items-center gap-2 text-xs font-medium text-desk-ink">
            Le navigateur redémarre : la page en cours sera perdue.
            <button
              type="button"
              onClick={() => {
                setConfirmReopen(false);
                void reopenForms();
              }}
              className={twMerge(BUTTON, NEUTRAL)}
            >
              Confirmer
            </button>
            <button
              type="button"
              onClick={() => setConfirmReopen(false)}
              className="text-xs font-medium text-desk-muted underline decoration-desk-ring underline-offset-[3px] hover:text-desk-strong"
            >
              Annuler
            </button>
          </span>
        )}
        {trail}
      </div>
      {screen && <WorkshopScreenViewer screen={screen} onClose={close} />}
      {screen && (
        <p className="text-xs text-desk-muted">
          {screen.formsMode} {screen.formsLastReopen}
        </p>
      )}
      {notice && <p role="status" className="text-sm text-desk-strong">{notice}</p>}
      {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}
